import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import type { GameMode, ChoiceType } from '../hooks/useGameLogic';
import ResultModal from './ResultModal';

interface CardGameProps {
  mode: GameMode;
  currentPlayer: string;
  isCardFlipped: boolean;
  currentChoice: ChoiceType | null;
  currentContent: string | null;
  onFlipCard: () => void;
  onChooseType: (type: ChoiceType) => void;
  onNextTurn: () => void;
  turnCount: number;
}

const modeThemes: Record<'soft' | 'hard', {
  bgGradient: string;
  cardBg: string;
  cardBackBg: string;
  buttonQuestion: string;
  buttonAction: string;
  glowClass: string;
  accentGradient: string;
}> = {
  soft: {
    bgGradient: 'from-blue-800 via-blue-900 to-[#0a1628]',
    cardBg: 'from-blue-500 to-blue-700',
    cardBackBg: 'from-green-400 via-emerald-400 to-emerald-500',
    buttonQuestion: 'from-green-400 to-emerald-500 shadow-[0_4px_20px_rgba(74,222,128,0.4)]',
    buttonAction: 'from-emerald-400 to-green-500 shadow-[0_4px_20px_rgba(52,211,153,0.4)]',
    glowClass: 'glow-soft',
    accentGradient: 'from-green-400 to-emerald-500',
  },
  hard: {
    bgGradient: 'from-purple-950 via-[#1a0533] to-[#0a0118]',
    cardBg: 'from-violet-700 to-purple-900',
    cardBackBg: 'from-violet-500 via-purple-600 to-blue-600',
    buttonQuestion: 'from-violet-500 to-blue-500 shadow-[0_4px_20px_rgba(124,58,237,0.4)]',
    buttonAction: 'from-purple-600 to-violet-500 shadow-[0_4px_20px_rgba(139,92,246,0.4)]',
    glowClass: 'glow-purple',
    accentGradient: 'from-violet-500 to-blue-500',
  },
};

// Riffle shuffle animation timeline (total 700ms)
const SHUFFLE_DURATION = 700;
const STATE_RESET_DELAY = 260;   // Reset game state just before new card springs in
const HAPTIC_DELAY = 640;        // Fire haptic just as card lands

export default function CardGame({
  mode,
  currentPlayer,
  isCardFlipped,
  currentChoice,
  currentContent,
  onFlipCard,
  onChooseType,
  onNextTurn,
  turnCount,
}: CardGameProps) {
  const [showResult, setShowResult] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const shuffleTimers = useRef<number[]>([]);
  const theme = modeThemes[mode as 'soft' | 'hard'];

  useEffect(() => {
    const timers = shuffleTimers.current;
    return () => {
      timers.forEach(id => window.clearTimeout(id));
    };
  }, []);

  const handleChoose = (type: ChoiceType) => {
    onChooseType(type);
    setShowResult(true);
  };

  const handleCloseResult = () => {
    setShowResult(false);
  };

  const triggerHaptic = useCallback(async () => {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch {
      // No-op on web or if Haptics unavailable
    }
  }, []);

  const handleNextTurn = useCallback(() => {
    if (isShuffling) return;
    setShowResult(false);
    setIsShuffling(true);

    // Reset game state mid-animation so the new card springs in face-down
    shuffleTimers.current.push(
      window.setTimeout(() => {
        onNextTurn();
      }, STATE_RESET_DELAY)
    );

    // Fire haptic right as the new card lands
    shuffleTimers.current.push(
      window.setTimeout(() => {
        triggerHaptic();
      }, HAPTIC_DELAY)
    );

    // End the shuffle sequence
    shuffleTimers.current.push(
      window.setTimeout(() => {
        setIsShuffling(false);
      }, SHUFFLE_DURATION)
    );
  }, [isShuffling, onNextTurn, triggerHaptic]);

  const cardSizeStyle = { width: 'min(70vw, 280px)', height: 'min(50vh, 360px)' } as const;

  return (
    <div className={`h-screen bg-gradient-to-b ${theme.bgGradient} relative`}>
      {/* Background particles for hard mode */}
      {mode === 'hard' && <HardModeParticles />}

      {/* Turn counter — below Dynamic Island */}
      <div className="absolute right-6 z-10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>
        <span className="text-white/30 font-body text-sm">Tour {turnCount + 1}</span>
      </div>

      {/* Main content — vertically centered, responsive card */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 z-10 safe-top safe-bottom">
        {/* Card stack container — holds main card + ghost cards */}
        <div
          className="relative mb-6"
          style={cardSizeStyle}
        >
          {/* Ghost cards — only visible during shuffle */}
          {isShuffling && (
            <>
              <GhostCard
                className="shuffle-ghost-back"
                theme={theme}
                mode={mode}
                style={cardSizeStyle}
              />
              <GhostCard
                className="shuffle-ghost-left"
                theme={theme}
                mode={mode}
                style={cardSizeStyle}
              />
              <GhostCard
                className="shuffle-ghost-right"
                theme={theme}
                mode={mode}
                style={cardSizeStyle}
              />
            </>
          )}

          {/* Main card — exits right during shuffle, springs in when new turn starts */}
          <div
            className={`card-flip absolute inset-0 ${
              isShuffling
                ? isCardFlipped
                  ? 'shuffle-exit'
                  : 'shuffle-enter'
                : ''
            }`}
            onClick={!isCardFlipped && !isShuffling ? onFlipCard : undefined}
          >
            <div className={`card-flip-inner ${isCardFlipped ? 'flipped' : ''}`}>
              {/* Card Front (face down) */}
              <div className={`card-front bg-gradient-to-br ${theme.cardBg} ${theme.glowClass} flex items-center justify-center cursor-pointer shimmer`}>
                <div className="text-center">
                  <div className="text-6xl mb-4">{mode === 'soft' ? '🍺' : '💀'}</div>
                  <p className="font-display font-bold text-white/80 text-lg">
                    Touche pour<br/>retourner
                  </p>
                </div>
              </div>

              {/* Card Back (face up - shows player name) */}
              <div className={`card-back bg-gradient-to-br ${theme.cardBackBg} flex items-center justify-center`}>
                <div className="text-center px-6">
                  <p className="text-white/70 font-body text-sm mb-2">C'est au tour de</p>
                  <h2 className="font-display font-black text-4xl text-white mb-1 drop-shadow-lg">
                    {currentPlayer}
                  </h2>
                  <div className="w-16 h-1 bg-white/30 rounded-full mx-auto mt-4" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Question / Action buttons */}
        {isCardFlipped && !currentChoice && !isShuffling && (
          <div className="flex gap-4 animate-slide-up">
            <button
              onClick={() => handleChoose('question')}
              className={`px-8 py-4 rounded-2xl bg-gradient-to-r ${theme.buttonQuestion} text-white font-display font-bold text-base btn-press`}
            >
              Question ❓
            </button>
            <button
              onClick={() => handleChoose('action')}
              className={`px-8 py-4 rounded-2xl bg-gradient-to-r ${theme.buttonAction} text-white font-display font-bold text-base btn-press`}
            >
              Action ⚡
            </button>
          </div>
        )}

        {/* Next turn button */}
        {currentChoice && !showResult && !isShuffling && (
          <button
            onClick={handleNextTurn}
            className={`px-10 py-4 rounded-2xl bg-gradient-to-r ${theme.accentGradient} text-white font-display font-bold text-base btn-press animate-slide-up`}
          >
            Tour suivant →
          </button>
        )}
      </div>

      {/* Result modal */}
      {currentChoice && currentContent && (
        <ResultModal
          isOpen={showResult}
          type={currentChoice}
          content={currentContent}
          playerName={currentPlayer}
          mode={mode}
          onClose={handleCloseResult}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ghost card — same visual style as face-down card, used during shuffle
// ---------------------------------------------------------------------------
interface GhostCardProps {
  className: string;
  theme: typeof modeThemes['soft'];
  mode: GameMode;
  style: React.CSSProperties;
}

function GhostCard({ className, theme, mode, style }: GhostCardProps) {
  return (
    <div
      className={`absolute inset-0 rounded-[20px] bg-gradient-to-br ${theme.cardBg} ${theme.glowClass} pointer-events-none ${className}`}
      style={style}
    >
      <div className="w-full h-full flex items-center justify-center rounded-[20px] overflow-hidden">
        <div className="text-center opacity-80">
          <div className="text-6xl mb-4">{mode === 'soft' ? '🍺' : '💀'}</div>
        </div>
      </div>
    </div>
  );
}

function HardModeParticles() {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 5 + 3,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full bg-violet-400/20"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animation: `float ${p.duration}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}
